import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  AudioSettings,
  getAudioSettings,
  saveAudioSettings,
  playSound as playSoundUtil,
  loadTrack,
  sleep,
  SoundType,
  preloadAllSfx,
  clearSfxCache,
} from '~/lib/audio';

interface AudioContextValue {
  settings: AudioSettings;
  updateSettings: (
    settings: Partial<AudioSettings>
  ) => void;
  playSound: (sound: SoundType, volume?: number) => void;
  currentTrack: string | null;
}

const AudioContext =
  createContext<AudioContextValue | null>(null);

export function useAudio() {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error(
      'useAudio must be used within AudioProvider'
    );
  }
  return context;
}

const LOGIN_ROUTES = [
  '/login',
  '/signup',
  '/reset-password',
];
const SHOPPING_ROUTES = [
  '/shoppe',
  '/shoppe/body',
  '/shoppe/hair',
  '/shoppe/personality',
  '/shoppe/race',
  '/shoppe/skin',
];

const GENERAL_PLAYLIST = [
  "/audio/bgm/general/Wanderer's Tale.mp3",
  '/audio/bgm/general/Sunlight Through Leaves.mp3',
  '/audio/bgm/general/Golden Gleam.mp3',
  '/audio/bgm/general/Forgotten Biomes.mp3',
  '/audio/bgm/general/waiting for a hope.mp3',
  '/audio/bgm/general/Music_Loop_5_Full.ogg',
];

const SHOPPING_PLAYLIST = {
  intro: '/audio/bgm/shopping/Wine for my solitude.mp3',
  loopers: ['/audio/bgm/shopping/Music_Loop_3_Full.ogg'],
};

const LOGIN_TRACK =
  '/audio/bgm/intro_page/Music_Loop_1.ogg';

export function AudioProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [settings, setSettings] = useState<AudioSettings>(
    getAudioSettings
  );
  const [currentTrack, setCurrentTrack] = useState<
    string | null
  >(null);

  const currentAudioRef = useRef<HTMLAudioElement | null>(
    null
  );
  const playlistIndexRef = useRef(0);
  const isShoppingIntroRef = useRef(false);
  const isCrossfadingRef = useRef(false);
  const trackCacheRef = useRef<
    Map<string, HTMLAudioElement>
  >(new Map());

  const location = useLocation();

  const updateSettings = useCallback(
    (newSettings: Partial<AudioSettings>) => {
      setSettings((prev: AudioSettings) => {
        const updated = { ...prev, ...newSettings };
        saveAudioSettings(updated);

        if ('sfxEnabled' in newSettings) {
          if (newSettings.sfxEnabled) {
            preloadAllSfx();
          } else {
            clearSfxCache();
          }
        }

        if (currentAudioRef.current) {
          if ('musicEnabled' in newSettings) {
            if (
              newSettings.musicEnabled &&
              currentAudioRef.current.paused
            ) {
              currentAudioRef.current
                .play()
                .catch(() => {});
            } else if (
              !newSettings.musicEnabled &&
              !currentAudioRef.current.paused
            ) {
              currentAudioRef.current.pause();
              trackCacheRef.current.clear();
            }
          }
          if ('musicVolume' in newSettings) {
            currentAudioRef.current.volume =
              newSettings.musicVolume!;
          }
        }

        return updated;
      });
    },
    []
  );

  const playSound = useCallback(
    (sound: SoundType, volume?: number) => {
      playSoundUtil(sound, volume);
    },
    []
  );

  const getOrLoadTrack = useCallback(
    async (url: string): Promise<HTMLAudioElement> => {
      if (trackCacheRef.current.has(url)) {
        return trackCacheRef.current.get(url)!;
      }

      const audio = await loadTrack(url);
      trackCacheRef.current.set(url, audio);
      return audio;
    },
    []
  );

  const crossfade = useCallback(
    async (
      fromTrack: HTMLAudioElement | null,
      toTrackUrl: string,
      duration = 1500
    ) => {
      if (isCrossfadingRef.current) return;
      isCrossfadingRef.current = true;

      try {
        const toTrack = await getOrLoadTrack(toTrackUrl);
        toTrack.volume = 0;
        toTrack.currentTime = 0;

        const steps = 50;
        const interval = duration / steps;
        await toTrack.play();

        const fromVolume = fromTrack?.volume ?? 0;
        const toVolume = settings.musicVolume;

        for (let i = 0; i <= steps; i++) {
          const progress = i / steps;
          if (fromTrack && !fromTrack.paused) {
            fromTrack.volume = fromVolume * (1 - progress);
          }
          toTrack.volume = toVolume * progress;
          await sleep(interval);
        }

        if (fromTrack) {
          fromTrack.pause();
          fromTrack.currentTime = 0;
        }

        currentAudioRef.current = toTrack;
        setCurrentTrack(toTrackUrl);
      } catch (e) {
        console.warn('[BGM] Crossfade failed:', e);
      } finally {
        isCrossfadingRef.current = false;
      }
    },
    [settings.musicVolume, getOrLoadTrack]
  );

  const playNextInPlaylist = useCallback(
    async (playlist: string[], shouldLoop = true) => {
      if (!settings.musicEnabled) return;

      const nextIndex =
        (playlistIndexRef.current + 1) % playlist.length;

      if (!shouldLoop && nextIndex === 0) {
        return;
      }

      playlistIndexRef.current = nextIndex;
      await crossfade(
        currentAudioRef.current,
        playlist[nextIndex]
      );
    },
    [settings.musicEnabled, crossfade]
  );

  const handleTrackEnded = useCallback(async () => {
    const path = location.pathname;
    if (
      SHOPPING_ROUTES.some(route => path.includes(route))
    ) {
      if (isShoppingIntroRef.current) {
        const randomLooper =
          SHOPPING_PLAYLIST.loopers[
            Math.floor(
              Math.random() *
                SHOPPING_PLAYLIST.loopers.length
            )
          ];
        isShoppingIntroRef.current = false;

        const looper = await getOrLoadTrack(randomLooper);
        looper.loop = true;
        await crossfade(
          currentAudioRef.current,
          randomLooper
        );
      }
    } else if (!LOGIN_ROUTES.includes(path)) {
      await playNextInPlaylist(GENERAL_PLAYLIST, true);
    }
  }, [
    location.pathname,
    crossfade,
    getOrLoadTrack,
    playNextInPlaylist,
  ]);

  useEffect(() => {
    if (!settings.musicEnabled) {
      if (
        currentAudioRef.current &&
        !currentAudioRef.current.paused
      ) {
        currentAudioRef.current.pause();
      }
      return;
    }

    const path = location.pathname;
    let targetTrack: string | null = null;
    let shouldLoop = false;

    if (LOGIN_ROUTES.includes(path)) {
      targetTrack = LOGIN_TRACK;
      shouldLoop = true;
    } else if (
      SHOPPING_ROUTES.some(route => path.includes(route))
    ) {
      // Check if we're already playing a shopping looper
      const isPlayingShoppingLooper =
        SHOPPING_PLAYLIST.loopers.some(
          looper => currentTrack === looper
        );

      if (isPlayingShoppingLooper) {
        // Already on a looper, don't reset to intro
        targetTrack = currentTrack;
      } else {
        // Not on a looper yet, play intro
        targetTrack = SHOPPING_PLAYLIST.intro;
        isShoppingIntroRef.current = true;
      }
    } else {
      // Check if we're already playing a track in the General playlist
      const currentTrackIndex = GENERAL_PLAYLIST.findIndex(
        track => track === currentTrack
      );

      if (currentTrackIndex !== -1) {
        // Already playing a track from this playlist, maintain it
        targetTrack = currentTrack;
      } else {
        // Not playing from this playlist, start from beginning
        targetTrack = GENERAL_PLAYLIST[0];
        playlistIndexRef.current = 0;
      }
    }

    if (targetTrack && targetTrack !== currentTrack) {
      crossfade(currentAudioRef.current, targetTrack).then(
        () => {
          if (currentAudioRef.current) {
            currentAudioRef.current.loop = shouldLoop;
          }
        }
      );
    }
  }, [
    location.pathname,
    settings.musicEnabled,
    currentTrack,
    crossfade,
  ]);

  useEffect(() => {
    const audio = currentAudioRef.current;
    if (!audio) return;

    audio.addEventListener('ended', handleTrackEnded);
    return () => {
      audio.removeEventListener('ended', handleTrackEnded);
    };
  }, [handleTrackEnded, currentAudioRef.current]);

  return (
    <AudioContext.Provider
      value={{
        settings,
        updateSettings,
        playSound,
        currentTrack,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
}
